import React, { useEffect, useRef, useState } from 'react';
import { QrCode, TriangleAlert, MessageSquareText, CheckCheck, ShieldCheck } from 'lucide-react';
import { useLocale } from '../../context/LocaleContext';

/**
 * Hero "Sinyal" demosu — bir yabancının aracın için yaktığı uyarı sinyalinin yolculuğu:
 * 1) cama yapışık QR taranır → 2) hatalı park mesajı seçilir → 3) telefon kilit ekranı
 * amber sinyalle "yanar" → 4) okunmazsa yedek SMS. Vurgu HATALI PARK (çekiliyor değil).
 * Sanat yönü: amber sinyal (tek cesur renk) + yeşile çalan sıcak-siyah gece + fiziksel sticker.
 * prefers-reduced-motion: sekans anında son kareye (uyarı görünür) düşer, animasyon yok.
 */

const STAGE_DURATIONS = [2000, 2200, 2800, 3200]; // ms

const TEXTS = {
  tr: {
    steps: ['QR okutuluyor', 'Mesaj seçiliyor', 'Sinyal iletildi', 'Yedek SMS'],
    scanHint: 'Uygulama gerekmez',
    // HATALI PARK lider — kullanıcı isteği
    templates: ['Hatalı park — yolu kapatıyorsunuz', 'Farlarınız açık kalmış', 'Aracınız çekiliyor'],
    pushBody: 'Hatalı park! Aracınız yolu kapatıyor — 34 ABC 123',
    now: 'şimdi',
    anonSender: 'Gönderenin kimliği gizli',
    smsLabel: '1 dk içinde okunmadı → Yedek SMS',
    smsBody: 'PARKMESAJ: Hatali park! Araciniz yolu kapatiyor. 34 ABC 123',
    anonymous: 'ANONİM',
    tryIt: 'Kendiniz deneyin ↓',
    lockDate: 'Salı, 12 Ağustos',
    stickerLabel: 'Cam üstündeki etiket',
  },
  en: {
    steps: ['Scanning QR', 'Picking message', 'Signal delivered', 'Backup SMS'],
    scanHint: 'No app needed',
    templates: ['Improper parking — blocking the way', 'Headlights left on', 'Your car is being towed'],
    pushBody: 'Improper parking! You are blocking the way — 34 ABC 123',
    now: 'now',
    anonSender: "Sender's identity is hidden",
    smsLabel: 'Unread in 1 min → Backup SMS',
    smsBody: 'PINGPARKING: Improper parking! You are blocking the way. 34 ABC 123',
    anonymous: 'ANONYMOUS',
    tryIt: 'Try it yourself ↓',
    lockDate: 'Tue, Aug 12',
    stickerLabel: 'Sticker on the windshield',
  },
};

export default function HeroDemo() {
  const { locale } = useLocale();
  const t = TEXTS[locale === 'en' ? 'en' : 'tr'];
  const [stage, setStage] = useState(0);
  const [reduced, setReduced] = useState(false);
  const pausedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setReduced(true);
      setStage(2); // statik: uyarı görünür
      return;
    }
    let cancelled = false;
    const advance = (current: number) => {
      timerRef.current = setTimeout(() => {
        if (cancelled) return;
        if (pausedRef.current) { advance(current); return; }
        const next = (current + 1) % 4;
        setStage(next);
        advance(next);
      }, STAGE_DURATIONS[current]);
    };
    advance(0);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const selectStage = (i: number) => { if (!reduced) setStage(i); };

  return (
    <div
      className="relative w-full max-w-[540px] select-none"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
    >
      {/* Amber sinyal ışıması — tek anlamlı hareket (partikül yok) */}
      <div
        className="absolute inset-0 -z-10 blur-3xl transition-opacity duration-700"
        style={{
          opacity: stage >= 2 ? 0.85 : 0.5,
          background: 'radial-gradient(ellipse 60% 55% at 62% 42%, rgba(255,176,32,0.30) 0%, rgba(255,176,32,0.10) 42%, transparent 74%)',
        }}
      />

      <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
        {/* ── Sol: fiziksel QR sticker + mesaj seçimi ── */}
        <div className="signal-panel rounded-3xl p-5 relative overflow-hidden">
          <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[#34C3A0] bg-[rgba(52,195,160,0.12)] border border-[rgba(52,195,160,0.28)] px-2 py-1 rounded-full">
            <ShieldCheck size={10} /> {t.anonymous}
          </div>

          <div className="text-[10px] font-mono tracking-wider text-[#8f8b7d] mb-3 uppercase">{t.stickerLabel}</div>
          <div className={`relative inline-flex items-center gap-2.5 bg-[#fdfbf4] rounded-xl p-2.5 shadow-xl transition-all duration-500 ${stage === 0 ? 'ring-4 ring-[#FFB020]/60 scale-105' : 'ring-0'}`}>
            <QrCode size={42} className="text-[#14170f]" strokeWidth={1.6} />
            <div className="pr-1">
              <div className="text-[#14170f] text-[10px] font-black leading-tight">PARK<span className="text-[#d98a00]">MESAJ</span></div>
              <div className="text-[#7c786d] text-[7.5px] leading-tight">{t.scanHint}</div>
            </div>
            {stage === 0 && !reduced && <div className="demo-scanline absolute inset-x-0 top-0 h-0.5 bg-[#FFB020] shadow-[0_0_12px_2px_rgba(255,176,32,0.85)]" />}
          </div>

          <div className={`mt-4 space-y-1.5 transition-opacity duration-500 ${stage >= 1 ? 'opacity-100' : 'opacity-30'}`}>
            {t.templates.map((tpl, i) => (
              <div
                key={i}
                className={`text-[11px] rounded-lg px-2.5 py-1.5 border transition-all duration-300 flex items-center gap-1.5 ${
                  stage >= 1 && i === 0
                    ? 'border-[#FFB020] bg-[rgba(255,176,32,0.14)] text-white font-semibold'
                    : 'border-white/10 bg-white/[0.04] text-[#9a968c]'
                }`}
              >
                {i === 0 && <TriangleAlert size={12} className={stage >= 1 ? 'text-[#FFB020]' : 'text-[#6b6759]'} />}
                <span className="flex-1">{tpl}</span>
                {stage >= 1 && i === 0 && <CheckCheck size={12} className="text-[#FFB020]" />}
              </div>
            ))}
          </div>
        </div>

        {/* ── Sağ: sürücünün telefonu (kilit ekranı) ── */}
        <div className={`w-[196px] rounded-[2rem] border-[6px] border-[#24271d] bg-[#0a0c08] shadow-2xl overflow-hidden ${stage === 2 && !reduced ? 'demo-shake' : ''}`}>
          <div className="flex items-center justify-between px-4 pt-2 pb-1">
            <span className="text-[9px] text-[#8f8b7d] font-semibold">09:41</span>
            <div className="w-12 h-3 bg-[#24271d] rounded-full" />
            <span className="text-[9px] text-[#8f8b7d]">🔋</span>
          </div>

          <div className="px-2.5 pb-4 pt-2 min-h-[224px] relative">
            <div className="text-center mb-3">
              <div className="text-[#e9e6da] font-light text-[2rem] leading-none tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>09:41</div>
              <div className="text-[9px] text-[#8f8b7d] mt-0.5">{t.lockDate}</div>
            </div>

            <div className={`relative rounded-xl bg-white/[0.06] border p-2.5 backdrop-blur-sm transition-all duration-500 ${stage >= 2 ? 'translate-y-0 opacity-100 border-[rgba(255,176,32,0.4)]' : '-translate-y-5 opacity-0 border-white/10'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="rounded-[5px] bg-[#FFB020] flex items-center justify-center" style={{ width: 18, height: 18 }}>
                  <TriangleAlert size={11} className="text-[#241700]" strokeWidth={2.4} />
                </span>
                <span className="text-[8px] text-white font-extrabold flex-1 truncate tracking-wide">PARK<span className="text-[#FFB020]">MESAJ</span> · {t.now}</span>
                <span className="text-[8px] text-[#8f8b7d]">09:41</span>
              </div>
              <p className="text-[10px] text-[#f3f0e6] font-semibold leading-snug">{t.pushBody}</p>
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className="text-[7.5px] font-bold text-[#34C3A0] border border-[rgba(52,195,160,0.4)] rounded-full px-1.5 py-[1px]">{t.anonymous}</span>
                <span className="text-[8px] text-[#8f8b7d]">{t.anonSender}</span>
              </div>
            </div>

            <div className={`relative mt-2.5 transition-all duration-500 ${stage >= 3 || reduced ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
              <div className="text-[8px] text-[#FFB020]/90 font-semibold mb-1 flex items-center gap-1">
                <MessageSquareText size={9} /> {t.smsLabel}
              </div>
              <div className="rounded-xl rounded-tl-sm bg-[#34C3A0]/12 border border-[#34C3A0]/28 p-2">
                <p className="text-[9px] text-[#7fe6cd] leading-snug font-mono">{t.smsBody}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-4 gap-1.5">
        {t.steps.map((label, i) => (
          <button
            key={i}
            type="button"
            onClick={() => selectStage(i)}
            className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB020]/60 rounded"
            aria-label={label}
          >
            <div className={`h-1 rounded-full mb-1.5 transition-colors duration-300 ${i <= stage ? 'bg-[#FFB020]' : 'bg-white/10 group-hover:bg-white/25'}`} />
            <span className={`text-[9px] leading-tight block transition-colors duration-300 ${i === stage ? 'text-white font-semibold' : 'text-[#8f8b7d]'}`}>
              {label}
            </span>
          </button>
        ))}
      </div>

      <a href="#demo" className="mt-3 inline-block text-[11px] text-[#FFB020] hover:underline font-semibold">
        {t.tryIt}
      </a>
    </div>
  );
}
